// src/lib/storage.js
// Phase 37 — extracted from src/App.jsx (lines 568–570, 619–751, 2549–2557, 2992–3007).
//
// Scope: localStorage I/O for the tend:* prefix family + done:/dismiss: keys.
// Public exports: storageKey, dismissKey, monthOffset, safeSetItem,
//                 persistOnboardingProgress, markOnboardingComplete,
//                 persistOnboardField, persistOnboardArray, persistOnboardJSON,
//                 migrateGardenType, wipeAllTendData, countAllTasks.
// Module state: none.

import { CURRENT_YEAR } from '../constants.js';

export function storageKey(plantId, type, month, year) { return `done:${plantId}:${type}:${month}:${year}`; }
export function dismissKey(plantId, type, month, year) { return `dismiss:${plantId}:${type}:${month}:${year}`; }
export function monthOffset(base, offset) { let m = base + offset, y = CURRENT_YEAR; if (m < 1) { m += 12; y -= 1; } if (m > 12) { m -= 12; y += 1; } return { month: m, year: y }; }

// Safe localStorage write wrapper — catches QuotaExceededError instead of silently failing.
// Non-quota errors are re-thrown so callers can observe unexpected failures.
export function safeSetItem(key, value, onError) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
      if (onError) onError('storage-full');
    } else {
      throw e;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ONBOARDING HELPERS (Phase 21 / ONB-01)
// ─────────────────────────────────────────────────────────────────────────────
export function persistOnboardingProgress(step) {
  try {
    localStorage.setItem('tend:onboardingProgress', JSON.stringify({ step, updatedAt: new Date().toISOString() }));
  } catch {}
}
export function markOnboardingComplete() {
  try {
    localStorage.setItem('tend:onboarded', new Date().toISOString());
    localStorage.removeItem('tend:onboardingProgress');
  } catch {}
}

// Returns the appropriate first-load action for the onboarding wizard, given the
// current localStorage state. Pure decision function — no side effects — so it is
// trivially unit-testable. Caller applies the action.
//
//   'open'        — brand-new user; show the wizard from step 0
//   'mark-done'   — returning user with plant data but no flag; set the flag,
//                   skip the wizard (pre-Phase-21 users, CSV-import users,
//                   users who dismissed without "meaningful progress")
//   'skip'        — already onboarded, mid-wizard, or empty + has progress
export function resolveOnboardingFirstLoadAction(storage = (typeof localStorage !== 'undefined' ? localStorage : null)) {
  if (!storage) return 'skip';
  let onboarded = null, progress = null, plantsRaw = null;
  try { onboarded = storage.getItem('tend:onboarded'); } catch {}
  try { progress  = storage.getItem('tend:onboardingProgress'); } catch {}
  try { plantsRaw = storage.getItem('tend:plants'); } catch {}
  if (onboarded) return 'skip';
  let hasPlants = false;
  if (plantsRaw) {
    try {
      const parsed = JSON.parse(plantsRaw);
      hasPlants = Array.isArray(parsed) && parsed.length > 0;
    } catch { hasPlants = false; }
  }
  if (hasPlants) return 'mark-done';
  if (progress) return 'skip';
  return 'open';
}
export function persistOnboardField(key, value) {
  try {
    if (value && String(value).trim()) localStorage.setItem(key, String(value).trim());
  } catch {}
}

// Multi-value persister for tend:gardenSpaces (JSON array). Writes only when array non-empty.
export function persistOnboardArray(key, arr) {
  try {
    if (Array.isArray(arr) && arr.length > 0) {
      localStorage.setItem(key, JSON.stringify(arr));
    }
  } catch {}
}

// One-way migration: legacy tend:gardenType (string) → tend:gardenSpaces (JSON array). Idempotent.
// Removes the legacy key unconditionally after read — the array is now the source of truth.
export function migrateGardenType() {
  try {
    const legacy = localStorage.getItem('tend:gardenType');
    const current = localStorage.getItem('tend:gardenSpaces');
    if (legacy && !current) {
      localStorage.setItem('tend:gardenSpaces', JSON.stringify([legacy]));
    }
    if (legacy) localStorage.removeItem('tend:gardenType');
  } catch {}
}

// Writes a JSON blob under key; removes the key if value is null/undefined.
export function persistOnboardJSON(key, value) {
  try {
    if (value == null) { localStorage.removeItem(key); return; }
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

// Phase 31.1 Plan 02 (#208 D-05.2) — Wipe all tend:* localStorage keys.
// Uses the prefix-scan pattern (not a hardcoded list) so future `tend:*`
// keys are swept automatically. Returns the count of keys removed so
// callers can log telemetry and tests can assert the wipe size.
// Non-tend keys are left untouched.
export function wipeAllTendData() {
  const keys = Object.keys(localStorage).filter(k => k.startsWith('tend:'));
  keys.forEach(k => localStorage.removeItem(k));
  return keys.length;
}

// Phase 31.1 Plan 02 (#208 D-05) — Sum every task across every plant.
// Used by the Delete all data modal's live-count message and unit tests.
// Defensive against null/undefined plants or missing tasks objects.
export function countAllTasks(plants) {
  if (!Array.isArray(plants)) return 0;
  return plants.reduce((sum, p) => {
    const monthly = p && p.tasks && typeof p.tasks === 'object' ? p.tasks : {};
    return sum + Object.values(monthly).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
  }, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 40 Plan 03 — WAL + dead-letter helpers (CONTEXT D-13/D-14, RESEARCH §6).
// ─────────────────────────────────────────────────────────────────────────────
// `tend:wal` — FIFO ops log appended on every signed-in state mutation.
// `tend:wal-deadletter` — ops that failed sync three times; user-visible via
// SyncStatusIndicator + DeadLetterModal.
//
// All helpers use try/catch + safeSetItem so quota errors and parse failures
// degrade silently rather than crashing the calling state mutator.

const WAL_KEY = 'tend:wal';
const WAL_DL_KEY = 'tend:wal-deadletter';

export function readWal() {
  try { return JSON.parse(localStorage.getItem(WAL_KEY) || '[]'); }
  catch { return []; }
}

export function writeWal(wal) {
  try { safeSetItem(WAL_KEY, JSON.stringify(wal)); } catch {}
}

export function pushWalOp({ op, table, cloudId, payload }) {
  try {
    const wal = readWal();
    wal.push({
      op,
      table,
      cloudId,
      payload,
      ts: Date.now(),
      attempts: 0,
      last_modified: new Date().toISOString(),
    });
    safeSetItem(WAL_KEY, JSON.stringify(wal));
  } catch {}
}

// Peek without remove — caller drains via removeWalOps once Worker /sync ACKs.
export function popWalBatch(maxN) {
  const wal = readWal();
  return wal.slice(0, maxN);
}

// Remove ops by (ts, cloudId, op) tuple — references survive JSON round-trip.
export function removeWalOps(opsToRemove) {
  try {
    const wal = readWal();
    const remove = new Set(opsToRemove.map((o) => `${o.ts}|${o.cloudId}|${o.op}`));
    const remaining = wal.filter((o) => !remove.has(`${o.ts}|${o.cloudId}|${o.op}`));
    writeWal(remaining);
  } catch {}
}

export function readDeadLetter() {
  try { return JSON.parse(localStorage.getItem(WAL_DL_KEY) || '[]'); }
  catch { return []; }
}

function writeDeadLetter(arr) {
  try { safeSetItem(WAL_DL_KEY, JSON.stringify(arr)); } catch {}
}

export function pushDeadLetter(op, reason) {
  try {
    const dl = readDeadLetter();
    dl.push({ ...op, deadLetterReason: reason, deadLetterAt: Date.now() });
    safeSetItem(WAL_DL_KEY, JSON.stringify(dl));
  } catch {}
}

export function getDeadLetterCount() {
  return readDeadLetter().length;
}

// Discard by `${ts}|${cloudId}` keys — the IDs surfaced via DeadLetterModal.
export function discardDeadLetter(opIds) {
  try {
    const dl = readDeadLetter();
    const remove = new Set(opIds);
    writeDeadLetter(dl.filter((o) => !remove.has(`${o.ts}|${o.cloudId}`)));
  } catch {}
}
